import React, { useEffect, useState } from "react";
import { styled, useStyletron } from "baseui";
import { Button } from "baseui/button";
import { Input, SIZE } from "baseui/input";
import {
  ParagraphMedium,
  ParagraphSmall,
  ParagraphLarge,
} from "baseui/typography";
import { connect } from "react-redux";
import {
  fetchOrganization,
  updateOrganization,
  fetchOrganizationStat,
} from "../../redux/user/UserThunk";
import { FaCreditCard, FaTachometerAlt } from "react-icons/fa"; // Importing a credit card icon for payment indication
import { useTranslation } from "react-i18next";
import { createOrganizationAccountSession, createDahsboardLoginSession } from "../../helpers/PaymentHelpers";

const Container = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  height: "100%",
  maxWidth: "800px", // Increase the maxWidth of the parent container
  margin: "0 auto",
});
const ListContainer = styled("div", {
  flex: 1, // Occupy the remaining available space in the parent container
  width: "100%",
  overflowY: "auto",
  maxHeight: "600px",
});
const FormContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

const FieldContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "1px",
});

const UpperSection = styled("div", {
  maxWidth: "400px", // Set the desired maximum width for the upper section
  width: "100%", // Add this property to ensure the section takes the full available width
  margin: "0 auto",
});
const LowerSection = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  maxWidth: "400px",
  margin: "50px auto",
});
const SaveButton = styled(Button, {
  marginTop: "48px",
  width: "100px",
});
const RedirectButton = styled(Button, ({ $theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: $theme.colors.primary,
  color: $theme.colors.contentInversePrimary,
  ":hover": {
    backgroundColor: $theme.colors.primary700,
  },
  padding: $theme.sizing.scale500,
  boxShadow: $theme.lighting.shadow400,
  textDecoration: "none",
  marginTop: "24px",
  gap: "8px", // Add a gap between the icon and text
}));
const UserAccountSettingsView = (props) => {
  const [Settings, setSettings] = useState([]);
  const {t} = useTranslation('common');
  const [newOrganizationName, setNewOrganizationName] = useState(
    props.primary_org_name
  );
  const [managePaymentLoading, setManagePaymentLoading] = useState(false);
  const [viewDashboardLoading, setViewDashboardLoading] = useState(false);


  useEffect(() => {
    props.getOrganization(props.primary_org_id);
    props.getOrganizationStat(props.primary_org_id);
  }, [props.primary_org_id]);

  useEffect(() => {
    setNewOrganizationName(props.primary_org_name);
  }, [props.primary_org_name]);

  const handleManagePaymentAccount = async () => {
    setManagePaymentLoading(true);
    try {
      const res = await createOrganizationAccountSession();
      if (res) {
        window.location.href = res;
      }
    } catch (error) {
      console.error("Failed to create checkout session:", error);
    } finally {
      setManagePaymentLoading(false);
    }
  };

    const handleViewDashboard = async () => {
      setViewDashboardLoading(true);
      try {
        const res = await createDahsboardLoginSession();
        if (res) {
          window.location.href = res;
        }
      } catch (error) {
        console.error("Failed to redirect to the dashboard:", error);
      } finally {
        setViewDashboardLoading(false);
      }
    };

  return (
    <Container>
      <UpperSection>
        <FormContainer>
          <FieldContainer>
            <label htmlFor="organization-name">{t('organizationName.label')}</label>
            <ParagraphSmall>
              {t('organizationName.description')}
            </ParagraphSmall>
            <Input
              size={SIZE.mini}
              value={newOrganizationName}
              onChange={(e) => setNewOrganizationName(e.target.value)}
            />
          </FieldContainer>
          <FieldContainer>
            <label htmlFor="organization-id">{t('organizationId.label')}</label>
            <ParagraphSmall>
              {t('organizationId.description')}
            </ParagraphSmall>
            <Input
              size={SIZE.mini}
              id="organization-id"
              value={props.primary_org_id}
              disabled
            />
          </FieldContainer>
          <SaveButton
            size={SIZE.mini}
            isLoading={props.user?.saveOrgInProgress}
            shape="pill"
            onClick={() =>
              props.updateOrganization({
                id: props.primary_org_id,
                name: newOrganizationName,
              })
            }
          >
            {t('saveButton')} 
          </SaveButton>
        </FormContainer>
      </UpperSection>
      <LowerSection>
        <RedirectButton
          size={SIZE.default}
          onClick={handleManagePaymentAccount}
          isLoading={managePaymentLoading}
          disabled={managePaymentLoading || props.user?.subscription?.plan !== "PREMIUM"}
        >
          <FaCreditCard /> 
          {t('managePaymentAccountButton')}
        </RedirectButton>
        
        
        <RedirectButton
          size={SIZE.default}
          onClick={handleViewDashboard}
          isLoading={viewDashboardLoading}
          disabled={viewDashboardLoading || props.user?.subscription?.plan !== "PREMIUM"}        
        >
        <FaTachometerAlt /> 
          {t('viewAccountDashboardButton')}
        </RedirectButton>
      </LowerSection>
    </Container>
  );
};
const mapStateToProps = (state) => {
  return {
    user: state.user,
    primary_org_name: state.user.primaryOrg?.name,
    primary_org_id: state.user.primaryOrgId,
    usageData: state.user.orgStat,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getOrganization: (org_id) => dispatch(fetchOrganization(org_id)),
    getOrganizationStat: (org_id) => dispatch(fetchOrganizationStat(org_id)),
    updateOrganization: (organization) =>
      dispatch(updateOrganization(organization)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UserAccountSettingsView);
